'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import { XIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { Button, type ButtonProps } from '../_base/Button'
import {
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverClose,
  PopoverContent
} from '../_base/Popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '../_base/Tooltip'
import { Text, type TextProps } from '../typography/Text'

const FeatureCalloutAnchorContext =
  React.createContext<React.RefObject<HTMLDivElement | null> | null>(null)

function useFeatureCalloutAnchor() {
  const anchorRef = React.use(FeatureCalloutAnchorContext)

  if (!anchorRef) {
    throw new Error('FeatureCallout components must be used within FeatureCallout')
  }

  return anchorRef
}

interface FeatureCalloutDismiss {
  /** Registered by `FeatureCalloutClose` so Escape can route through the same control. */
  closeButtonRef: React.RefObject<HTMLButtonElement | null>
  /** Records that the pending close came from the user, so focus returns to the anchor. */
  markUserDismissal: () => void
}

const FeatureCalloutDismissContext = React.createContext<FeatureCalloutDismiss | null>(null)

function useFeatureCalloutDismiss() {
  const dismiss = React.use(FeatureCalloutDismissContext)

  if (!dismiss) {
    throw new Error('FeatureCalloutClose must be used within FeatureCalloutContent')
  }

  return dismiss
}

// The severity tokens are ~8% alpha tints meant to sit inline on an opaque page, but the callout
// floats (the popover is transparent and this inner div paints the surface + shadow), so a raw
// tint would let table rows behind it bleed through. `primary` is a near-solid brand color, so we
// only strip its baked-in alpha; the severity variants composite their saturated `-foreground`
// over the opaque popover surface — reproducing the banner wash while staying opaque in both themes.
// `neutral` is the plain popover surface — already opaque, so it gets the regular border instead
// of a tint.
const calloutSurfaceVariants = cva('relative rounded-xl p-4 shadow-lg', {
  variants: {
    variant: {
      neutral: 'border-border bg-popover text-popover-foreground border',
      primary: 'bg-[rgb(from_var(--primary)_r_g_b/1)] text-primary-foreground',
      info: 'bg-[color-mix(in_srgb,var(--info-foreground)_8%,var(--popover))] text-info-foreground',
      warning:
        'bg-[color-mix(in_srgb,var(--warning-foreground)_8%,var(--popover))] text-warning-foreground',
      destructive:
        'bg-[color-mix(in_srgb,var(--destructive-foreground)_8%,var(--popover))] text-destructive-foreground'
    }
  },
  defaultVariants: { variant: 'primary' }
})

const calloutArrowVariants = cva('', {
  variants: {
    variant: {
      neutral: 'fill-popover',
      primary: 'fill-[rgb(from_var(--primary)_r_g_b/1)]',
      info: 'fill-[color-mix(in_srgb,var(--info-foreground)_8%,var(--popover))]',
      warning: 'fill-[color-mix(in_srgb,var(--warning-foreground)_8%,var(--popover))]',
      destructive: 'fill-[color-mix(in_srgb,var(--destructive-foreground)_8%,var(--popover))]'
    }
  },
  defaultVariants: { variant: 'primary' }
})

// The close and action controls render in the surface's accent color. `current` resolves to the
// `text-*-foreground` the surface sets, so their border/hover/active/focus tints track the variant
// with no per-variant table — only the surface fill and its matching arrow differ by variant.
const CALLOUT_CLOSE_CLASS =
  'shrink-0 hover:bg-current/10 active:bg-current/15 focus-visible:border-current focus-visible:ring-current/50'

const CALLOUT_BUTTON_CLASS =
  'w-fit border-current/50 text-current hover:bg-current/10 active:bg-current/15 focus-visible:border-current focus-visible:ring-current/50'

// Header and body are rows, not blocks, so a second child — typically `FeatureCalloutClose` — is
// pushed to the far edge with a small min gap that keeps long copy from running into it. With a
// single child the row behaves exactly like the block it replaces. `items-start` keeps the control
// on the first line when the copy beside it wraps.
//
// Every child is a flex item, and JSX splits adjacent text and expressions into separate children:
// `{name} matures soon` is two items, so they would spread apart. Content assembled from more than
// one node has to be wrapped in a single element to read as one run of text.
const CALLOUT_ROW_CLASS = 'gap-sm flex items-start justify-between'

/** Surface treatment for a feature callout, mirroring `Alert`'s variants plus `neutral`. */
export type FeatureCalloutVariant = NonNullable<
  VariantProps<typeof calloutSurfaceVariants>['variant']
>

export type FeatureCalloutProps = Omit<React.ComponentProps<typeof Popover>, 'modal'>

/**
 * Non-modal coach-mark root. Supports controlled and uncontrolled open state while keeping
 * outside interaction available.
 */
export function FeatureCallout(props: FeatureCalloutProps) {
  const anchorRef = React.useRef<HTMLDivElement>(null)

  return (
    <FeatureCalloutAnchorContext value={anchorRef}>
      <Popover {...props} modal={false} />
    </FeatureCalloutAnchorContext>
  )
}

export type FeatureCalloutAnchorProps = Omit<
  React.ComponentProps<typeof PopoverAnchor>,
  'asChild' | 'ref'
> & {
  children: React.ReactElement
}

/**
 * Attaches a callout to an existing control and records that control as the focus-restoration
 * target. The child must accept a ref.
 */
export function FeatureCalloutAnchor(props: FeatureCalloutAnchorProps) {
  const anchorRef = useFeatureCalloutAnchor()

  return <PopoverAnchor {...props} ref={anchorRef} asChild />
}

type LockedContentProps =
  | 'onCloseAutoFocus'
  | 'onEscapeKeyDown'
  | 'onFocusOutside'
  | 'onInteractOutside'
  | 'onKeyDownCapture'
  | 'onOpenAutoFocus'
  | 'onPointerDownOutside'

export type FeatureCalloutContentProps = Omit<
  React.ComponentProps<typeof PopoverContent>,
  LockedContentProps | 'aria-label' | 'role'
> & {
  'aria-label': string
  /** Surface treatment for the surface, arrow, and inner controls. Defaults to `primary`. */
  variant?: FeatureCalloutVariant
}

/**
 * Collision-aware callout surface. An accessible label is required because the surface is
 * announced as a dialog. The arrow is always included; compose `FeatureCalloutClose` into a
 * header or body row to give the callout a dismiss control.
 */
export function FeatureCalloutContent({
  'aria-label': ariaLabel,
  align = 'center',
  children,
  className,
  collisionPadding = 12,
  side = 'top',
  sideOffset = 12,
  variant = 'primary',
  onClick,
  ...props
}: FeatureCalloutContentProps) {
  const anchorRef = useFeatureCalloutAnchor()
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)
  // Whether the current close was the user activating the dismiss control (directly, or via
  // Escape routed to it). Only such closes restore focus to the anchor — programmatic closes
  // (a controlled `open` flip, e.g. another callout opening or a cross-tab dismissal) must
  // leave focus wherever the user is working.
  const isUserDismissalRef = React.useRef(false)
  const dismiss: FeatureCalloutDismiss = {
    closeButtonRef,
    markUserDismissal: () => {
      isUserDismissalRef.current = true
    }
  }

  return (
    <FeatureCalloutDismissContext value={dismiss}>
      <PopoverContent
        {...props}
        aria-label={ariaLabel}
        align={align}
        collisionPadding={collisionPadding}
        role="dialog"
        side={side}
        sideOffset={sideOffset}
        className={cn(
          // z-40 keeps the ambient coach mark under modal layers (dialog overlay/content are
          // z-50) — the base popover's z-50 would win the tie against an earlier-mounted dialog.
          'z-40 max-w-[calc(100vw-var(--spacing-lg))] border-0 bg-transparent p-0 shadow-none',
          className
        )}
        // The content portals to the body but stays in the React tree under whatever it is anchored
        // to (e.g. a clickable table row), so clicks would bubble through the React tree to that
        // ancestor. Run the consumer's handler, then stop the event from reaching the content behind.
        onClick={event => {
          onClick?.(event)
          event.stopPropagation()
        }}
        onCloseAutoFocus={event => {
          event.preventDefault()
          if (isUserDismissalRef.current) anchorRef.current?.focus()
          isUserDismissalRef.current = false
        }}
        onInteractOutside={event => event.preventDefault()}
        onKeyDownCapture={event => {
          if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            // Radix closes on Escape from its own `document` listener registered with
            // `{ capture: true }`, so it has already run by the time this fires and cannot be
            // called off from here. Route through the dismiss control when one is composed, so
            // Escape and a click take the same path (including a consumer's `onClick` veto).
            // With no control to route through, record the dismissal directly — otherwise the
            // content closes unflagged and focus falls to the body instead of the anchor.
            const closeButton = closeButtonRef.current
            if (closeButton) closeButton.click()
            else dismiss.markUserDismissal()
          }
        }}
        // Coach marks announce, they don't interrupt: opening must not move focus (it would
        // also instant-open the dismiss control's tooltip alongside the callout itself).
        onOpenAutoFocus={event => event.preventDefault()}
      >
        <div className={calloutSurfaceVariants({ variant })} data-slot="feature-callout-surface">
          {/* Header, body, and every control inherit the surface's `text-*-foreground`; the
              controls pick it up through `current` rather than a per-variant class. */}
          <div className="gap-sm flex flex-col">{children}</div>
          <PopoverArrow className={calloutArrowVariants({ variant })} />
        </div>
      </PopoverContent>
    </FeatureCalloutDismissContext>
  )
}

// `asChild` and `ref` are omitted rather than inherited: the icon child is fixed (so slotting
// would hand the control's props to the SVG and lose button semantics) and the ref is spoken for
// by the Escape routing below.
export type FeatureCalloutCloseProps = Omit<
  ButtonProps,
  'asChild' | 'children' | 'fullWidth' | 'ref' | 'size' | 'tooltip' | 'variant'
>

/**
 * Dismiss control, and the target Escape routes through. Compose it as the last child of a
 * `FeatureCalloutHeader` or `FeatureCalloutBody`, whose row layout pushes it to the far edge —
 * a callout without a title puts it in the body instead. Omit it for a callout the consumer
 * closes on its own terms (its `open` prop), which also leaves Escape to the popover.
 */
export function FeatureCalloutClose({ className, onClick, ...props }: FeatureCalloutCloseProps) {
  const { closeButtonRef, markUserDismissal } = useFeatureCalloutDismiss()

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <PopoverClose asChild>
          <Button
            {...props}
            ref={closeButtonRef}
            aria-label="Dismiss"
            className={cn(CALLOUT_CLOSE_CLASS, className)}
            data-slot="feature-callout-close"
            // `PopoverClose` composes its close through Radix's `composeEventHandlers`, which
            // skips it when the consumer's handler calls `preventDefault()`. Arm the flag on the
            // same condition: a cancelled close must not leave it set for a later programmatic
            // close to read as a user dismissal and steal focus back to the anchor.
            onClick={event => {
              onClick?.(event)
              if (!event.defaultPrevented) markUserDismissal()
            }}
            size="iconSm"
            variant="ghost"
          >
            <XIcon />
          </Button>
        </PopoverClose>
      </TooltipTrigger>
      <TooltipContent>Dismiss</TooltipContent>
    </Tooltip>
  )
}

export type FeatureCalloutHeaderProps = Omit<TextProps, 'color' | 'size' | 'tag'>

/**
 * Regular-m callout heading. Inherits the surface's variant color. A second child — typically
 * `FeatureCalloutClose` — sits at the opposite edge of the row.
 */
export function FeatureCalloutHeader({ children, className, ...props }: FeatureCalloutHeaderProps) {
  return (
    <Text
      {...props}
      className={cn(CALLOUT_ROW_CLASS, className)}
      data-slot="feature-callout-header"
      size="md"
      tag="span"
    >
      {children}
    </Text>
  )
}

export type FeatureCalloutBodyProps = Omit<TextProps, 'color' | 'size' | 'tag'>

/**
 * Regular-xs supporting copy for a feature callout. Inherits the surface's variant color. A
 * second child — typically `FeatureCalloutClose` on a titleless callout — sits at the opposite
 * edge of the row.
 */
export function FeatureCalloutBody({ children, className, ...props }: FeatureCalloutBodyProps) {
  return (
    <Text
      {...props}
      className={cn(CALLOUT_ROW_CLASS, className)}
      data-slot="feature-callout-body"
      size="xs"
    >
      {children}
    </Text>
  )
}

export type FeatureCalloutButtonProps = Omit<
  ButtonProps,
  'fullWidth' | 'size' | 'tooltip' | 'variant'
>

/** Outline-small callout action. Activating it does not dismiss the callout. */
export function FeatureCalloutButton({ className, ...props }: FeatureCalloutButtonProps) {
  return (
    <Button
      {...props}
      className={cn(CALLOUT_BUTTON_CLASS, className)}
      data-slot="feature-callout-button"
      size="sm"
      variant="outline"
    />
  )
}
